import {
  type SymbolExpoAccountPositionValue, type SymbolExpoExposureAsset,
  type SymbolExposureData, type SymbolExpoSymbolPositionValue
} from '../../../models/exposure/exposure-types'
import { type ExposureDataGridRow } from '../../../pages/exposure/exposure-data-grid'

const DELIMITER = '&&'

export function createSymbolExposureRows (symbolExposureData: SymbolExposureData): ExposureDataGridRow[] {
  const symbolExpoExposureAsset: SymbolExpoExposureAsset[] = symbolExposureData.exposure_asset
  const symbolExpoSymbolPositionValue: SymbolExpoSymbolPositionValue[] = symbolExposureData.symbol_positionvalue

  const uniqueSymbolAccountExposureSums: Record<string, number> = {}
  const uniqueSymbolExposureUsdSums: Record<string, number> = {}
  const uniqueSymbolNetUsdSums: Record<string, number> = {}

  for (let i = 0; i < symbolExpoExposureAsset.length; i++) {
    const item = symbolExpoExposureAsset[i]
    const { amount } = item
    const symbolName = item.symbol_name
    const currPrice = item.curr_price
    const diffPrice = item.diff_price
    const tag = symbolName

    if (Number.isFinite(uniqueSymbolAccountExposureSums[tag])) {
      uniqueSymbolAccountExposureSums[tag] += Number(amount)
      uniqueSymbolExposureUsdSums[tag] += Number(currPrice)
      uniqueSymbolNetUsdSums[tag] += Number(diffPrice)
    } else {
      uniqueSymbolAccountExposureSums[tag] = Number(amount)
      uniqueSymbolExposureUsdSums[tag] = Number(currPrice)
      uniqueSymbolNetUsdSums[tag] = Number(diffPrice)
    }
  }

  const uniqueSymbolPositionValues: Record<string, string> = {}

  for (let i = 0; i < symbolExpoSymbolPositionValue.length; i++) {
    const item = symbolExpoSymbolPositionValue[i]
    const tag = item.symbol_name

    uniqueSymbolPositionValues[tag] = item.position_value
  }

  const symbolRows: ExposureDataGridRow[] = Object.keys(uniqueSymbolAccountExposureSums)
    .map((symbol) => {
      return {
        id: symbol,
        path: [symbol],
        coin: '',
        account: '',
        account_type: '',
        symbol,
        account_exposure: uniqueSymbolAccountExposureSums[symbol].toString(),
        price_usd: '',
        exposure_coin: '',
        exposure_usd: uniqueSymbolExposureUsdSums[symbol].toString(),
        initial_exposure_coin: '',
        net_coin: '',
        net_usd: uniqueSymbolNetUsdSums[symbol].toString(),
        position_value: uniqueSymbolPositionValues[symbol],
      }
    })

  return symbolRows
    .concat(createSymbolCoinExposureRows(symbolExposureData))
    .concat(createSymbolCoinAccountExposureRows(symbolExposureData))
}

function createSymbolCoinExposureRows (symbolExposureData: SymbolExposureData): ExposureDataGridRow[] {
  const symbolExpoExposureAsset: SymbolExpoExposureAsset[] = symbolExposureData.exposure_asset

  const coinPrice: Record<string, string> = {}
  const uniqueSymbolCoinAccountExposureSums: Record<string, number> = {}
  const uniqueSymbolCoinExposureCoinSums: Record<string, number> = {}
  const uniqueSymbolCoinExposureUsdSums: Record<string, number> = {}
  const uniqueSymbolCoinInitialExposureCoinSums: Record<string, number> = {}
  const uniqueSymbolCoinNetCoinSums: Record<string, number> = {}
  const uniqueSymbolCoinNetUsdSums: Record<string, number> = {}

  for (let i = 0; i < symbolExpoExposureAsset.length; i++) {
    const item = symbolExpoExposureAsset[i]
    const { amount, coin, difference } = item
    const symbolName = item.symbol_name
    const currPrice = item.curr_price
    const diffPrice = item.diff_price
    const initAmt = item.init_amt
    const tag = `${String(symbolName)}${DELIMITER}${String(coin)}`

    if (Number.isFinite(uniqueSymbolCoinAccountExposureSums[tag])) {
      uniqueSymbolCoinAccountExposureSums[tag] += Number(amount)
      uniqueSymbolCoinExposureCoinSums[tag] += Number(amount)
      uniqueSymbolCoinExposureUsdSums[tag] += Number(currPrice)
      uniqueSymbolCoinInitialExposureCoinSums[tag] += Number(initAmt)
      uniqueSymbolCoinNetCoinSums[tag] += Number(difference)
      uniqueSymbolCoinNetUsdSums[tag] += Number(diffPrice)
    } else {
      uniqueSymbolCoinAccountExposureSums[tag] = Number(amount)
      uniqueSymbolCoinExposureCoinSums[tag] = Number(amount)
      uniqueSymbolCoinExposureUsdSums[tag] = Number(currPrice)
      uniqueSymbolCoinInitialExposureCoinSums[tag] = Number(initAmt)
      uniqueSymbolCoinNetCoinSums[tag] = Number(difference)
      uniqueSymbolCoinNetUsdSums[tag] = Number(diffPrice)
    }

    if (!Object.hasOwn(coinPrice, coin)) {
      coinPrice[coin] = item.price
    }
  }

  return Object.keys(uniqueSymbolCoinAccountExposureSums)
    .map((symbolCoin) => {
      const split = symbolCoin.split(DELIMITER)
      const symbol = String(split[0])
      const coin = String(split[1])

      return {
        id: symbolCoin,
        path: [symbol, coin],
        coin,
        account: '',
        account_type: '',
        symbol: '',
        account_exposure: uniqueSymbolCoinAccountExposureSums[symbolCoin].toString(),
        price_usd: coinPrice[coin],
        exposure_coin: uniqueSymbolCoinExposureCoinSums[symbolCoin].toString(),
        exposure_usd: uniqueSymbolCoinExposureUsdSums[symbolCoin].toString(),
        initial_exposure_coin: uniqueSymbolCoinInitialExposureCoinSums[symbolCoin].toString(),
        net_coin: uniqueSymbolCoinNetCoinSums[symbolCoin].toString(),
        net_usd: uniqueSymbolCoinNetUsdSums[symbolCoin].toString(),
        position_value: ''
      }
    })
}

function createSymbolCoinAccountExposureRows (symbolExposureData: SymbolExposureData): ExposureDataGridRow[] {
  const symbolExpoExposureAsset: SymbolExpoExposureAsset[] = symbolExposureData.exposure_asset
  const symbolExpoAccountPositionValue: SymbolExpoAccountPositionValue[] = symbolExposureData.account_positionvalue

  const uniqueSymbolCoinAccountPositionValues: Record<string, string> = {}

  for (let i = 0; i < symbolExpoAccountPositionValue.length; i++) {
    const item = symbolExpoAccountPositionValue[i]
    const { coin, owner } = item
    const symbolName = item.symbol_name
    const tag = `${String(symbolName)}${DELIMITER}${String(coin)}${DELIMITER}${owner}`

    uniqueSymbolCoinAccountPositionValues[tag] = item.position_value
  }

  return symbolExpoExposureAsset
    .filter(item => !item.symbol_name.includes('BALANCE'))
    .map(item => {
      const tag = `${String(item.symbol_name)}${DELIMITER}${String(item.coin)}${DELIMITER}${String(item.owner)}`
      return {
        id: tag,
        path: [item.symbol_name, item.coin, item.owner],
        coin: '',
        account: item.owner,
        account_type: '',
        symbol: '',
        account_exposure: item.amount,
        price_usd: '',
        exposure_coin: item.amount,
        exposure_usd: item.curr_price,
        initial_exposure_coin: item.init_amt,
        net_coin: item.difference,
        net_usd: item.diff_price,
        position_value: uniqueSymbolCoinAccountPositionValues[tag]
      }
    })
}
